/** Copyright 2024 SAP SE or an SAP affiliate company. All rights reserved. */

import { z } from 'zod';

export const ProductDtoSchema = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string(),
    videoUrl: z.string(),
    logoUrl: z.string(),
    sku: z.number(),
    skuType: z.string().nullish(),
});

export const ProductRunDtoSchema = z.object({
    id: z.string(),
    realm: z.string().nullish(),
});

export const SubscriptionDtoSchema = z.object({
    productDto: ProductDtoSchema,
    runId: z.string().nullable(),
    launchTriggered: z.boolean(),
    questionCount: z.number(),
    answerCount: z.number(),
    flowId: z.string(),
    realm: z.string().nullish(),
});

export const ActivityDtoSchema = z.object({
    date: z.string(),
    description: z.string(),
});

export const LaunchStepStatusSchema = z.enum(['NOT_STARTED', 'RUNNING', 'FAILED', 'COMPLETED', 'COMPLETED_WITH_ERROR']);

export const LaunchStepDtoSchema = z.object({
    name: z.string(),
    status: LaunchStepStatusSchema,
    activity: z.array(ActivityDtoSchema),
});

export const LaunchSummaryDtoSchema = z.object({
    runId: z.string(),
    jobId: z.string(),
    status: LaunchStepStatusSchema,
    subStatus: LaunchStepStatusSchema.nullable(),
    steps: z.array(LaunchStepDtoSchema),
    tenantUrl: z.string(),
});

export const UserInfoDtoSchema = z.object({
    sub: z.string(),
    app_tid: z.string().optional(),
    mail: z.string(),
    iss: z.string(),
    groups: z.union([z.string(), z.array(z.string())]), // if user has multiple groups, then array of strings, else string
    given_name: z.string(),
    sid: z.string(),
    aud: z.string(),
    scim_id: z.string(),
    user_uuid: z.string(),
    crm_account_id: z.string(),
    exp: z.number(),
    iat: z.number(),
    family_name: z.string(),
    jti: z.string(),

    zone_uuid: z.string().optional(),
    company: z.string().optional(),
    nonce: z.string().optional(),
    locale: z.string().optional(),
    time_format: z.string().optional(),
    supported_crm_list: z.string().optional(),
});

export const UserDtoSchema = z.object({
    userName: z.string(),
    givenName: z.string(),
    familyName: z.string(),
    emailId: z.string(),
    role: z.string(),
    runId: z.string().optional(),
    id: z.string().optional(),
    isAnonymized: z.boolean().optional(),
    errorMsg: z.string().nullish(),
});

export const JobDto = z.object({
    detail: z.string(),
    id: z.string(),
    status: z.enum(['NOT_STARTED', 'RUNNING', 'FAILED', 'COMPLETED', 'COMPLETED_WITH_ERROR']),
    updatedAt: z.string(),
    createdAt: z.string(),
});

export const JobDetailsSchema = z.object({
    buyerAccountSBN: z.object({
        id: z.string(),
        businessTypeId: z.string(),
    }),
    s4HanaPC: z.object({
        id: z.string(),
        businessTypeId: z.string(),
    }),
});

export const PhaseDtoSchema = z.object({
    id: z.string(),
    flowId: z.string(),
    name: z.string(),
    displayOrder: z.number(),
    translationNameKey: z.string(),
    iconName: z.string(),
});

export const FlowDtoSchema = z.object({
    id: z.string(),
    name: z.string(),
    productId: z.string(),
    active: z.boolean(),
});

export const ControlTypeOptionDtoSchema = z.object({
    id: z.string(),
    controlTypeId: z.string(),
    value: z.string(),
    displayOrder: z.number(),
    translationValueKey: z.string().nullable(),
});

export const ControlTypeDtoSchema = z.object({
    id: z.string(),
    name: z.string(),
    orientation: z.string(),
    controlTypeOptions: z.array(ControlTypeOptionDtoSchema),
});

export const StepDtoSchema = z.object({
    id: z.string(),
    name: z.string(),
    phaseId: z.string(),
    displayOrder: z.number(),
    translationNameKey: z.string(),
    title: z.string(),
    description: z.string(),
    translationTitleKey: z.string(),
    learnMoreContent: z.string().nullable(),
});

export const TaskDtoSchema = z.object({
    id: z.string(),
    contentTypeId: z.string().nullable(),
    step: StepDtoSchema,
    phase: PhaseDtoSchema,
    flow: FlowDtoSchema,
    title: z.string(),
    helpLink: z.string().nullable(),
    description: z.string().nullable(),
    productConfigurationKey: z.string().nullable(),
    defaultValue: z.string().nullable(),
    mandatory: z.boolean(),
    displayOrder: z.number(),
    controlType: ControlTypeDtoSchema.nullable(),
    videoHelpLink: z.string().nullable(),
    iconLink: z.string().nullable(),
    translationTitleKey: z.string(),
    answer: z.any().optional(),
});

export const SelectIntegrationTaskSchema = z.object({
    buyerAccountSBN: z.object({
        id: z.string(),
        businessTypeId: z.string(),
    }),
    s4HanaPC: z.object({
        id: z.string(),
        businessTypeId: z.string(),
    }),
});

export const GetUserProductSubscriptionsResponseDtoSchema = z.array(SubscriptionDtoSchema);
export const GetUsersResponseDtoSchema = z.array(UserDtoSchema);
export const GetProductRunByUserIdAndFlowIdResponseDtoSchema = z.array(JobDto);
export const GetProductSubscriptionsResponseDtoSchema = z.array(SubscriptionDtoSchema);
export const GetTasksResponseDtoSchema = z.object({
    tasks: z.array(TaskDtoSchema),
    values: z.record(
        z.object({
            configurationValue: z.string().nullable(),
        })
    ),
});

export const SystemInfoDtoSchema = z.object({
    key: z.string(),
    value: z.string(),
});

export const FeatureFlagDtoSchema = z.object({
    httpStatus: z.number(),
    href: z.string(),
    type: z.string(),
    variation: z.string(),
});
